import { ActionIcon, Divider, FileButton, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import React, { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { globalConstants } from '../../../../constants/content'
import { convertDictToString, convertDictToText, convertUnitToBytes } from '../../../../utils/utils'
import RootButton from '../../../Buttons/RootButton'

interface AssetProps {
  image: File | null
  setImage: (image: File | null) => void
  nextStep: () => void
  exceedsImageSize: boolean
  invalidType: boolean
  setExceedsImageSize: (exceedsImageSize: boolean) => void
  setInvalidType: (invalidType: boolean) => void
  metadata: any
  totalDraftsSize?: number
  saveDraft: () => void
  openCropper: () => void
  setCropInfo: (cropInfo: any) => void
}

const Asset: React.FC<AssetProps> = ({
  image,
  setImage,
  nextStep,
  exceedsImageSize,
  invalidType,
  setExceedsImageSize,
  setInvalidType,
  metadata,
  saveDraft,
  openCropper,
  setCropInfo,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const assetRules = useMemo(() => {
    const jsonData = JSON.parse(JSON.stringify(metadata))
    return jsonData.asset.rules
  }, [metadata])

  const uploadImage = useCallback(
    (payload: File | null) => {
      if (payload) {
        const reader = new FileReader()
        reader.readAsDataURL(payload)
        reader.onload = () => {
          if (payload.size > convertUnitToBytes(assetRules.max, assetRules.unit.toUpperCase())) {
            setExceedsImageSize(true)
            setInvalidType(false)
            setImage(null)
          } else {
            setExceedsImageSize(false)
            setInvalidType(false)
            setImage(payload)
            openCropper()
            const img = new Image()
            img.src = window.URL.createObjectURL(payload)
            img.onload = () => {
              setCropInfo({
                x: 0,
                y: 0,
                width: img.width || globalConstants.CROP_IMAGE_SIZE.width,
                height: img.height || globalConstants.CROP_IMAGE_SIZE.height,
                rotate: 0,
                scaleX: 1,
                scaleY: 1,
              })
            }
          }
        }
      }
    },
    [setImage, setExceedsImageSize, assetRules]
  )

  const imageName = useMemo(() => {
    if (!image) {
      return null
    }
    return (
      !exceedsImageSize && (
        <>
          <Stack
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text style={{ textAlign: 'left' }}>{image.name}</Text>
            <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
              {image !== null && (
                <RootButton
                  style1={false}
                  secondary
                  onClick={() => {
                    openCropper()
                  }}
                  disabled={image === null}
                >
                  {t('buttons.edit', 'Edit')}
                </RootButton>
              )}
              <ActionIcon
                onClick={() => {
                  setImage(null)
                }}
              >
                <IconX size={20} />
              </ActionIcon>
            </Stack>
          </Stack>
          <Divider />
        </>
      )
    )
  }, [exceedsImageSize, image])

  return (
    <Stack>
      <Divider />
      <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
        <Title order={3}>{t('components.studio.asset.title', 'Item Image')}</Title>
        {/* <Text
          style={{
            color: theme.colors.textAndIcon[0],
            fontSize: '14px',
            fontFamily: 'monument-grotesk-regularitalic',
          }}
        >
          {t('components.studio.required', '* Required')}
        </Text> */}
      </Stack>
      <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
        <Trans i18nKey="components.studio.asset.description">
          Upload an image you’d like to publish and make publicly accessible on LAMINA1. Then select
          a cover image.
        </Trans>
      </Text>
      <Divider />
      {invalidType ? (
        <>
          <Text style={{ textAlign: 'left', color: theme.colors.notification[0] }}>
            <Trans i18nKey="components.studio.asset.invalidType">
              File type not accepted. Please upload a file with the following extensions:{' '}
            </Trans>
            {convertDictToText(assetRules.type)}
          </Text>
          <Divider />
        </>
      ) : (
        exceedsImageSize && (
          <>
            <Text style={{ textAlign: 'left', color: theme.colors.notification[0] }}>
              <Trans i18nKey="components.studio.asset.exceedsSize">
                Image exceeds {{ max: assetRules.max }}
                {{ unit: assetRules.unit }} upload size
              </Trans>
            </Text>
            <Divider />
          </>
        )
      )}
      {imageName}
      <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
        {image === null ? (
          <FileButton onChange={uploadImage} accept={convertDictToString(assetRules.type)}>
            {props => <RootButton {...props}>Upload</RootButton>}
          </FileButton>
        ) : (
          <RootButton
            onClick={() => {
              saveDraft()
              nextStep()
            }}
            disabled={!image}
          >
            {t('buttons.continue', 'Continue')}
          </RootButton>
        )}
      </Stack>
      <Trans i18nKey="components.studio.asset.original">
        <Text style={{ textAlign: 'left', color: theme.colors.paragraph[0] }}>
          By uploading this content, you confirm it is an original creation and adheres to our
          creator guidelines as part of the LAMINA1{' '}
          <a
            href="https://about.lamina1.com/terms-and-conditions---open-metaverse-limited"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>
          .
        </Text>
      </Trans>
    </Stack>
  )
}
export default Asset
